import { toast } from 'sonner';

import { useDeleteCompanyNoteMutation } from '@/modules/companies/mutations/useDeleteCompanyNoteMutation';
import { useUpdateCompanyNoteMutation } from '@/modules/companies/mutations/useUpdateCompanyNoteMutation';
import { NoteCard } from '@/modules/notes/components/NoteCard';
import type {
  CompanyNoteType,
  NoteFormData,
} from '@/modules/notes/components/NoteForm';
import type { components } from '@/types/apiSchema';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { companyNoteTypeOptions } from './CompanyNotesList';

type CompanyNoteResponse = components['schemas']['CompanyNoteResponse'];

interface CompanyNoteCardProps {
  note: CompanyNoteResponse;
  companyId: string;
}

export const CompanyNoteCard = ({ note, companyId }: CompanyNoteCardProps) => {
  const deleteNoteMutation = useDeleteCompanyNoteMutation();
  const updateNoteMutation = useUpdateCompanyNoteMutation();

  const handleUpdate = (data: NoteFormData) => {
    updateNoteMutation.mutate(
      {
        companyId,
        noteId: note.id,
        body: {
          content: data.content,
          type: data.type as CompanyNoteType,
        },
      },
      {
        onSuccess: () => {
          toast.success('Note updated successfully');
        },
        onError: (error) => {
          toast.error(`Failed to update note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleDelete = (noteId: string) => {
    deleteNoteMutation.mutate(
      { companyId, noteId },
      {
        onSuccess: () => {
          toast.success('Note deleted successfully');
        },
        onError: (error) => {
          toast.error(`Failed to delete note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  return (
    <NoteCard
      note={note}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isUpdating={updateNoteMutation.isPending}
      typeOptions={companyNoteTypeOptions}
    />
  );
};
